import { Component, Input, TrackByFunction } from '@angular/core'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { Identifiable } from '../../util'
import { Spacing } from '../util'
import { TableColumn } from './table-column'

export type VerticalBreakpoint = 'small' | 'medium' | 'none'

// Has an optional id field.
export type TableItem<TData> = TData & Partial<Identifiable>

/**
 * A simple table, based on our CSS Grid system
 */
@Component({
  selector: 'ftr-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
})
export class TableComponent<TData> {
  @Input()
  outerPadding = Spacing.Double

  /**
   * The column layout for this table
   *
   * Each column comprises of information such as column name, data, grid size etc.
   */
  @Input() columns: TableColumn<TData>[]

  @Input() hideHeader: boolean = false

  /**
   * How to detect duplicate rows, prevents complete table refreshes during change detection
   */
  @Input() trackBy: TrackByFunction<TableItem<TData>> = (index: number) => index
  /**
   * The actual row data, one row per item in this list.
   */
  @Input() items: TableItem<TData>[]

  /**
   * At what point we switch to vertical cards, rather than horizontal rows
   */
  @Input() verticalBreakpoint: VerticalBreakpoint = 'small'

  @Input() routerLinkFn: ((t: TData) => string[]) | undefined

  getColumnClasses(column: TableColumn<TData>): string {
    const clickableClass = this.routerLinkFn ? ' table-body__row--clickable' : ''
    switch (this.verticalBreakpoint) {
      case 'small':
        return `vertical-breakpoint--small col-sm-4 col-md-${column.gridSize}` + clickableClass
      case 'medium':
        return `vertical-breakpoint--medium col-sm-4 col-md-12 col-lg-${column.gridSize}` + clickableClass
      case 'none':
        return `vertical-breakpoint--none col-${column.gridSize}` + clickableClass
      default:
        assertUnreachable(this.verticalBreakpoint)
    }
  }

  getRowClasses(): string {
    let classes = `table-body--${this.verticalBreakpoint}`

    if (this.routerLinkFn) {
      classes += ' table-body--clickable'
    }

    if (this.hideHeader) {
      classes += ' table-body--no-header'
    }

    return classes
  }
}
